import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import findIndex from 'lodash/findIndex';

import {
    Button,
    Label,
    FormHelp,
    Text,
    Textarea,
} from '@zillow/constellation';

import {
    Col,
    HR,
    Link,
    Row,
} from '@zillow/zrs-comet';

import PropertiesActions from 'app/shared/actions/PropertiesActions';
import NotificationConstants from 'app/shared/constants/NotificationConstants';

import {
    StyledDatepicker,
} from './StyledComponents';

const propTypes = {
    setRef: PropTypes.func,
    specialOffers: PropTypes.array,
    updateStatusMessage: PropTypes.func,
};

const defaultProps = {
    setRef: () => { },
    specialOffers: [],
    updateStatusMessage: () => { },
};

const DATE = {
    END: 'end',
    START: 'start',
};

const ERROR = {
    DESCRIPTION: 'Description cannot be blank.',
};

class SpecialOffers extends React.Component {
    constructor(props) {
        super(props);

        this.addSpecialOffer = this.addSpecialOffer.bind(this);
        this.getDecoratedSpecialOffers = this.getDecoratedSpecialOffers.bind(this);
        this.handleDescriptionBlur = this.handleDescriptionBlur.bind(this);
        this.handleDateChange = this.handleDateChange.bind(this);
        this.updateOfferDescription = this.updateOfferDescription.bind(this);
        this.removeSpecialOffer = this.removeSpecialOffer.bind(this);
        this.isDescriptionValid = this.isDescriptionValid.bind(this);
        this.isSpecialOffersValid = this.isSpecialOffersValid.bind(this);
        this.isStartDateEnabled = this.isStartDateEnabled.bind(this);
        this.isEndDateEnabled = this.isEndDateEnabled.bind(this);
        this.saveSpecialOffers = this.saveSpecialOffers.bind(this);
        this.handleSpecialOfferCreate = this.handleSpecialOfferCreate.bind(this);
        this.handleSpecialOfferDelete = this.handleSpecialOfferDelete.bind(this);

        this.state = {
            isUpdating: false,
            specialOffers: this.getDecoratedSpecialOffers(this.props.specialOffers),
        };
    }

    componentDidMount() {
        // link parent component
        this.props.setRef(this);
    }

    componentWillReceiveProps(nextProps) {
        if (this.state.isUpdating && nextProps.specialOffers) {
            this.setState({ specialOffers: this.getDecoratedSpecialOffers(nextProps.specialOffers) });
        }
    }

    componentWillUnmount() {
        // unlink parent component
        this.props.setRef(undefined);
    }

    handleSpecialOfferCreate(params) {
        const { formData, updateFormData, updateStatusMessage } = this.props;
        const queryParams = {
            listingId: formData.listingId,
            includeListingDetails: true,
        };

        return this.props.dispatch(PropertiesActions.createOrUpdateSpecialOffer(params, queryParams))
            .then((response) => {
                // user has interacted with item
                response.isPristine = false;

                const copySpecialOffers = formData.specialOffers.slice();

                if (params.offerIdEncoded) {
                    // replace item
                    const doesOfferIdEncodedMatch = obj => obj.offerIdEncoded === params.offerIdEncoded;
                    const foundIndex = findIndex(copySpecialOffers, doesOfferIdEncodedMatch);

                    copySpecialOffers.splice(foundIndex, 1, response);
                } else {
                    copySpecialOffers.push(response);
                }

                updateFormData({ specialOffers: copySpecialOffers });
                updateStatusMessage('Special Offers Saved!', NotificationConstants.SUCCESS);
            })
            .catch((errorRMWModel) => {
                updateStatusMessage(errorRMWModel.message, NotificationConstants.ERROR);
            });
    }


    handleSpecialOfferDelete(offerId) {
        const { formData, updateFormData, updateStatusMessage } = this.props;
        if (!offerId) {
            return console.error('offerId must be supplied');
        }

        const queryParams = {
            listingId: formData.listingId,
            offerIdEncoded: offerId,
        };

        return this.props.dispatch(PropertiesActions.deleteSpecialOffer(queryParams))
            .then((response) => {
                if (response) {
                    const copySpecialOffers = formData.specialOffers.slice();
                    const foundIndex = findIndex(copySpecialOffers, obj => obj.offerIdEncoded === offerId);

                    if (foundIndex > -1) {
                        copySpecialOffers.splice(foundIndex, 1);

                        updateFormData({ specialOffers: copySpecialOffers });
                        updateStatusMessage('Special Offers Removed!', NotificationConstants.SUCCESS);
                    }
                }
            })
            .catch((errorRMWModel) => {
                updateStatusMessage(errorRMWModel.message, NotificationConstants.ERROR);
            });
    }


    addSpecialOffer() {
        if (this.state.specialOffers.length >= 5) {
            return;
        }

        const { specialOffers } = this.state;

        specialOffers.push({
            offerIdEncoded: null,
            description: null,
            startDateYyyyMmDd: null,
            endDateYyyyMmDd: null,
            isPristine: true,
            errorMessage: '',
        });

        this.setState({ specialOffers });
    }

    getDecoratedSpecialOffers(specialOffers) {
        return specialOffers.slice().map((item) => {
            item.isPristine = item.isPristine !== false;
            item.errorMessage = '';

            return item;
        });
    }

    handleDescriptionBlur(index) {
        const { specialOffers } = this.state;

        const copySpecialOffer = Object.assign({}, this.state.specialOffers[index]);
        copySpecialOffer.isPristine = false;

        let foundError = false;
        copySpecialOffer.errorMessage = '';

        if (!this.isDescriptionValid(copySpecialOffer)) {
            copySpecialOffer.errorMessage = ERROR.DESCRIPTION;
            foundError = true;
        }

        // update
        const copySpecialOffers = specialOffers.slice();
        copySpecialOffers[index] = copySpecialOffer;

        this.setState({ specialOffers: copySpecialOffers });

        // save
        if (!foundError) {
            this.saveSpecialOffers(copySpecialOffer);
        }
    }

    handleDateChange(i, value, datePosition) {
        const { specialOffers } = this.state;
        const copySpecialOffers = specialOffers.slice();
        const copySpecialOffer = copySpecialOffers[i];

        if (datePosition === DATE.START) {
            copySpecialOffer.startDateYyyyMmDd = format(value, 'YYYY-MM-DD');
        } else if (datePosition === DATE.END) {
            copySpecialOffer.endDateYyyyMmDd = format(value, 'YYYY-MM-DD');
        }

        copySpecialOffer.isPristine = !this.isDescriptionValid(copySpecialOffer);

        this.setState({ specialOffers: copySpecialOffers });

        if (!copySpecialOffer.isPristine && this.isSpecialOffersValid()) {
            this.saveSpecialOffers(copySpecialOffer);
        }
    }

    updateOfferDescription(event) {
        const value = event.target.value;
        const i = event.target.name;
        const { specialOffers } = this.state;
        const copySpecialOffers = specialOffers.slice();
        copySpecialOffers[i].description = value;
        copySpecialOffers[i].errorMessage = (!this.isDescriptionValid(copySpecialOffers[i]))
            ? ERROR.DESCRIPTION
            : '';

        this.setState({ specialOffers: copySpecialOffers });

        this.isSpecialOffersValid();
    }

    isDescriptionValid(specialOffer) {
        return specialOffer.description && specialOffer.description.trim() !== '';
    }

    isSpecialOffersValid() {
        const { specialOffers } = this.state;

        let foundError = false;
        specialOffers.forEach((specialOffer) => {
            // description is required
            if (!this.isDescriptionValid(specialOffer)) {
                specialOffer.errorMessage = ERROR.DESCRIPTION;
                foundError = true;
            }
        });

        return !foundError;
    }

    removeSpecialOffer(i) {
        this.props.updateStatusMessage('Removing Special Offer...');

        const copySpecialOffers = this.state.specialOffers.slice();
        const copySpecialOffer = copySpecialOffers[i];

        if (!copySpecialOffer.offerIdEncoded) {
            // remove from dom
            copySpecialOffers.splice(i, 1);

            this.setState({ specialOffers: copySpecialOffers });
        } else {
            // remove via request and update
            this.setState({ isUpdating: true });

            this.handleSpecialOfferDelete(copySpecialOffer.offerIdEncoded)
                .finally(() => {
                    this.setState({ isUpdating: false });
                });
        }
    }

    isStartDateEnabled(i, renderedDate) {
        const specialOffer = this.state.specialOffers[i];
        if (!specialOffer.endDateYyyyMmDd) {
            return true;
        }

        const endDateYyyyMmDd = new Date(specialOffer.endDateYyyyMmDd);
        const renderedDateObject = new Date(renderedDate);
        return renderedDateObject <= endDateYyyyMmDd;
    }

    isEndDateEnabled(i, renderedDate) {
        const specialOffer = this.state.specialOffers[i];
        if (!specialOffer.startDateYyyyMmDd) {
            return true;
        }

        const startDateYyyyMmDd = new Date(specialOffer.startDateYyyyMmDd);
        const renderedDateObject = new Date(renderedDate);
        return renderedDateObject >= startDateYyyyMmDd;
    }

    saveSpecialOffers(specialOffer) {
        this.props.updateStatusMessage('Saving Special Offers...');

        const params = {
            description: specialOffer.description,
            startDate: specialOffer.startDateYyyyMmDd,
            endDate: specialOffer.endDateYyyyMmDd,
        };

        if (specialOffer.offerIdEncoded) {
            params.offerIdEncoded = specialOffer.offerIdEncoded;
        }

        this.setState({ isUpdating: true });
        this.handleSpecialOfferCreate(params)
            .finally(() => {
                this.setState({ isUpdating: false });
            });
    }

    render() {
        const { specialOffers } = this.state;
        const { isInputFormDisabled } = this.props;

        return (
            <div className="SpecialOffers">
                <Text fontType="h4">
                    Special Offers
                </Text>
                {specialOffers.length > 0 && specialOffers.map((specialOffer, i) => {
                    const startDate = specialOffer.startDateYyyyMmDd
                                        && format(specialOffer.startDateYyyyMmDd, 'MM/DD/YYYY');

                    const endDate = specialOffer.endDateYyyyMmDd
                                        && format(specialOffer.endDateYyyyMmDd, 'MM/DD/YYYY');
                    return (
                        <div key={`special-offer-${i}`}>
                            <Row paddingY="xs">
                                <Col
                                    xs={12}
                                    md={6}
                                >
                                    <Label>
                                        Offer Start Date (mm/dd/yyyy)
                                    </Label>
                                    <StyledDatepicker
                                        id="startDatePicker"
                                        onChangeGetNativeFormat={date => this.handleDateChange(i, date, DATE.START)}
                                        getIsValidDate={renderedDate => this.isStartDateEnabled(i, renderedDate)}
                                        inputProps={{ placeholder: 'mm/dd/yyyy' }}
                                        value={startDate ? new Date(startDate) : null}
                                        disabled={isInputFormDisabled}
                                    />
                                </Col>

                                <Col
                                    xs={12}
                                    md={6}
                                >
                                    <Label>
                                        Offer End Date (mm/dd/yyyy)
                                    </Label>
                                    <StyledDatepicker
                                        id="endDatePicker"
                                        onChangeGetNativeFormat={date => this.handleDateChange(i, date, DATE.END)}
                                        getIsValidDate={renderedDate => this.isEndDateEnabled(i, renderedDate)}
                                        inputProps={{ placeholder: 'mm/dd/yyyy' }}
                                        value={endDate ? new Date(endDate) : null}
                                        disabled={isInputFormDisabled}
                                    />
                                </Col>
                            </Row>

                            <Row paddingY="xs">
                                <Col>
                                    <Textarea
                                        name={i}
                                        placeholder="Describe the special offer here. Do not use all-caps,
                                                                    abbreviations, jargon, or special characters."
                                        type="textarea"
                                        maxLength="400"
                                        resize="both"
                                        onBlur={() => this.handleDescriptionBlur(i)}
                                        onChange={this.updateOfferDescription}
                                        disabled={isInputFormDisabled}
                                        value={specialOffer.description || ''}
                                        error={specialOffer.errorMessage}
                                    />
                                    <FormHelp>Max 255 Characters</FormHelp>
                                    {specialOffer.errorMessage &&
                                    <FormHelp error={true}>{specialOffer.errorMessage}</FormHelp>
                                    }
                                </Col>
                            </Row>

                            <Row paddingY="xs">
                                <Col>
                                    <Link onClick={() => this.removeSpecialOffer(i)}>
                                        Remove this Special Offer
                                    </Link>
                                </Col>
                            </Row>

                            <HR />
                        </div>
                    );
                })}

                <Row paddingY="xs">
                    {specialOffers.length < 5 && (
                        <Col
                            md={3}
                            align="flex-start"
                        >
                            <Button
                                buttonType="primary"
                                onClick={this.addSpecialOffer}
                                data-test="add-specialoffer"
                                disabled={isInputFormDisabled}
                            >
                                + Add Offer
                            </Button>
                        </Col>
                    )}
                </Row>
            </div>
        );
    }
}

SpecialOffers.propTypes = propTypes;
SpecialOffers.defaultProps = defaultProps;

export default connect()(SpecialOffers);
